import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { EmptyState } from "@/shared/components/EmptyState";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import {
  categorizeRecord,
  toOverviewListItem,
  type OverviewCategoryKey,
  type OverviewListItem,
} from "@/features/patient/dashboard/utils/adaptHealthOverview";
import { cn } from "@/shared/lib/utils";

interface HealthOverviewChartProps {
  records: ParsedRecord[];
}

const CATEGORY_ORDER: OverviewCategoryKey[] = ["lab_result", "prescription", "visit_summary", "imaging", "immunization"];

const CATEGORY_META: Record<OverviewCategoryKey, { name: string; icon: string }> = {
  lab_result:    { name: "Results",       icon: "lucide:file-text" },
  prescription:  { name: "Prescriptions", icon: "lucide:pill" },
  visit_summary: { name: "Visits",        icon: "lucide:stethoscope" },
  imaging:       { name: "Imaging",       icon: "lucide:image" },
  immunization:  { name: "Immunizations", icon: "lucide:syringe" },
};

function formatItemDate(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0].payload;
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 shadow-lg">
        <p className="text-sm font-extrabold text-[#0A2947]">{name}</p>
        <p className="text-xs text-[#4B5563]">{value} record{value === 1 ? "" : "s"}</p>
      </div>
    );
  }
  return null;
};

const CustomBarLabel = (props: any) => {
  const { x, y, width, value, isActive } = props;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={12}
      fontWeight={isActive ? 800 : 500}
      fill={isActive ? "#0A2947" : "#9CA3AF"}
    >
      {value}
    </text>
  );
};

export function HealthOverviewChart({ records }: HealthOverviewChartProps) {
  const [hoveredBar, setHoveredBar] = useState<OverviewCategoryKey | null>(null);
  const [selectedBar, setSelectedBar] = useState<OverviewCategoryKey | null>(null);

  const byCategory = useMemo(() => {
    const buckets: Record<OverviewCategoryKey, ParsedRecord[]> = {
      lab_result: [], prescription: [], visit_summary: [], imaging: [], immunization: [],
    };
    for (const record of records) {
      const category = categorizeRecord(record);
      if (category) buckets[category].push(record);
    }
    return buckets;
  }, [records]);

  const data = CATEGORY_ORDER.map((key) => ({
    name: CATEGORY_META[key].name,
    value: byCategory[key].length,
    key,
  }));

  const yMax = Math.max(4, ...data.map((d) => d.value));

  // Default to the first column that actually has data, so results are visible
  // below the graph immediately — clicking a different column switches to it.
  const defaultBar = CATEGORY_ORDER.find((key) => byCategory[key].length > 0) ?? null;
  const activeBar = selectedBar ?? defaultBar;

  const handleBarClick = (entry: any) => {
    const key = entry?.activePayload?.[0]?.payload?.key as OverviewCategoryKey | undefined;
    if (!key) return;
    setSelectedBar(key);
  };

  const activeItems: OverviewListItem[] = activeBar
    ? byCategory[activeBar].map(toOverviewListItem).sort((a, b) => b.date.localeCompare(a.date))
    : [];

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
          barCategoryGap="30%"
          onClick={handleBarClick}
          style={{ cursor: "pointer" }}
        >
          <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="4 4" />
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: "#9CA3AF", fontWeight: 500 }}
            dy={8}
          />
          <YAxis
            domain={[0, yMax]}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
          <Bar
            dataKey="value"
            radius={[6, 6, 0, 0]}
            onMouseEnter={(entry) => setHoveredBar(entry.key)}
            onMouseLeave={() => setHoveredBar(null)}
            label={(props) => (
              <CustomBarLabel
                {...props}
                isActive={
                  data[props.index]?.key === hoveredBar ||
                  data[props.index]?.key === activeBar
                }
              />
            )}
          >
            {data.map((entry) => (
              <Cell
                key={entry.key}
                fill={
                  entry.key === hoveredBar || entry.key === activeBar
                    ? "#0A2947"
                    : "#E2E5EA"
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Results list — defaults to the first column with data, switches on click */}
      {activeBar && (
        <div className="mt-3">
          <p className="mb-2 text-sm font-extrabold tracking-tight text-[#0A2947]">
            {CATEGORY_META[activeBar].name} ({activeItems.length})
          </p>
          {activeItems.length === 0 ? (
            <EmptyState variant="compact" title="No records in this category yet." />
          ) : (
            // Height tuned to exactly 2 rows — a 3rd+ item scrolls instead of growing the card.
            <div className="max-h-32 overflow-y-auto rounded-xl border border-[#E5E7EB]">
              {activeItems.map((item, index) => {
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5",
                      index < activeItems.length - 1 && "border-b border-[#F3F4F6]"
                    )}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F36F8F]">
                      <Icon icon={CATEGORY_META[activeBar].icon} className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-extrabold text-[#0A2947]">{item.title}</p>
                      <p className="truncate text-xs text-[#4B5563]">
                        {item.detail}{item.date && ` · ${formatItemDate(item.date)}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
